<template>
  <div class="cb-multimedia--raw relative" :style="omit(style, 'top', 'left', 'width', 'height')">
    <ResizeHandles
      v-if="showHandles"
      :handles="['top-center', 'bottom-center']"
      maintain-aspect-ratio
      :model-value="style ?? {}"
      @update:model-value="updateDimensions"
    />
    <CImage
      v-if="data.type === 'image' && shouldShow"
      :key="url"
      class="flex multimedia-raw__img"
      :class="{
        'w-full': !isScribbleChild,
        'h-full': isScribbleChild,
        'rounded-2': isEditMode,
        ...justifyClassesObject,
      }"
      :img-props="{
        draggable: false,
        style: {
          userSelect: isScribbleChild ? 'none' : 'auto',
          width: isScribbleChild ? style?.width : 'auto',
          height: isScribbleChild ? style?.height : 'auto',
          maxWidth: '100%',
          borderTopLeftRadius: style?.borderTopLeftRadius,
          borderTopRightRadius: style?.borderTopRightRadius,
          borderBottomLeftRadius: style?.borderBottomLeftRadius,
          borderBottomRightRadius: style?.borderBottomRightRadius,
        },
      }"
      :lazy="lazy"
      :object-fit="isResizableEnabled ? 'contain' : 'cover'"
      :preview-disabled="isEditMode || isScribbleChild"
      :src="url"
      :style="omit(style, 'paddingBottom', 'paddingLeft', 'paddingTop', 'paddingRight')"
    />

    <CVideo
      v-else-if="data.type === 'video' && shouldShow"
      :key="url + posterUrl"
      :options="{
        poster: posterUrl,
        posterBackgroundSize,
        fluid: true,
        responsive: true,
        sources: [
          {
            src: url,
          },
        ],
      }"
      :style="omit(style, 'paddingBottom', 'paddingLeft', 'paddingTop', 'paddingRight')"
    />
  </div>
</template>

<script setup lang="ts">
import { omit } from 'lodash-es'
import { computed, inject, type CSSProperties, type ComputedRef } from 'vue'

import CImage from '@lib/components/CImage/CImage.vue'
import CVideo from '@lib/components/CVideo/CVideo.vue'
import ResizeHandles from '@canvas-builder/components/container/EditableComponent/ResizeHandles.vue'

import useCanvas from '@canvas-builder/composables/useCanvas'
import { useComponentStyle } from '@canvas-builder/composables/useComponentStyle'

import {
  ComponentTypes,
  MultimediaHorizontalAlignmentOptions,
  type ComponentNode,
  type MultimediaProps,
} from '@canvas-builder/types/canvas'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { CanvasRetrieve } from '@openapi'

const props = withDefaults(
  defineProps<{
    id: ComponentNode['id']
    data: MultimediaProps
    usedInSectionId?: CanvasRetrieve['id']
    lazy: boolean
    parentType?: ComponentTypes
    selected?: boolean
    style?: CSSProperties
  }>(),
  {
    lazy: true,
  },
)

const emit = defineEmits<{
  style: [s: Record<string, any>]
}>()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const componentNode = computed(() => (props.id ? componentNodesById.value[props.id] : null) as ComponentNode)

const isResizableEnabled = computed(() => !!launchDarkly.value.enable_resizable_multimedia_component)

const isScribbleChild = computed(() => {
  return props.parentType === ComponentTypes.Scribble
})

const url = computed(
  () => (props.data.asset_type === 'external' ? props.data.external_url : props.data?.file?.content_url) ?? '',
)
const posterUrl = computed(() => props.data.poster_url ?? '')
const posterBackgroundSize = computed(() => props.data.poster_background_size ?? 'cover')
const shouldShow = computed(() => {
  if (isEditMode.value) return true

  return !!url.value
})
const showHandles = computed(() => {
  return (
    !isScribbleChild.value && !props.usedInSectionId && isEditMode.value && props.selected && isResizableEnabled.value
  )
})

const { componentNodesById, isEditMode } = useCanvas()
const { createStyleUpdate } = useComponentStyle(componentNode)

const justifyClassesObject = computed(() => {
  return {
    'justify-start': props.data.horizontal_alignment === MultimediaHorizontalAlignmentOptions.LEFT,
    'justify-center':
      props.data.horizontal_alignment === MultimediaHorizontalAlignmentOptions.CENTER ||
      !props.data.horizontal_alignment,
    'justify-end': props.data.horizontal_alignment === MultimediaHorizontalAlignmentOptions.RIGHT,
  }
})

function updateDimensions(value: Record<string, any>) {
  // keep the 'width' value as 'auto' to avoid the component from being resized
  emit('style', createStyleUpdate({ ...value, width: 'auto' }))
}
</script>

<style lang="scss" scoped>
.cb-multimedia--raw {
  :deep(.video-js) {
    display: flex !important;
    justify-content: v-bind('props.data?.horizontal_alignment ?? "center"') !important;
    width: 100% !important;
    height: v-bind('isResizableEnabled ? props.style?.height : "auto"') !important;
    padding: 0 !important;

    .vjs-tech {
      position: static;
      max-width: 100%;
      width: 100%;
    }

    video,
    .vjs-poster img {
      display: block !important;
    }

    video {
      width: 100% !important;
      height: v-bind('isResizableEnabled ? props.style?.height : "auto"') !important;
      max-width: 100%;
    }

    .vjs-poster img {
      width: 100% !important;
      object-fit: v-bind('posterBackgroundSize ?? "cover"');
      height: 100%;
    }
  }
}
</style>
